"use client";

import { useEffect, useState } from "react";

import { ArrowIconTextlink } from "@/components/ui-kit/ButtonNova/Buttonicons";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { HOMEPAGE_HERO_MODE } from "@/constants/vwo";
import { cn } from "@/utils/general";
import { getPromoByName } from "@/utils/getPromoData";
import { segmentEvent } from "@/utils/segmentfunctions";

import styles from "./QuizResurfaceBanner.module.scss";
import QuizResurfaceBannerPanel from "./QuizResurfaceBannerPanel";
import { QuizResurfaceProduct, readQuizResurfaceProduct } from "./quizResurfaceProduct";

/*
 * Bundle image hero (NT2-33283): its art sits under the banner, so the wrapper overlays the hero.
 * False while the pill hero is the default (Black Friday, NT2-32946).
 * Turns true when HOMEPAGE_HERO_MODE (src/constants/vwo.ts) is set to "regular".
 */
const OVERLAY_HERO = HOMEPAGE_HERO_MODE === "regular";

type QuizResurfaceBannerProps = {
  productData?: QuizResurfaceProduct | null;
};

const QuizResurfaceBanner = ({ productData: productDataProp }: QuizResurfaceBannerProps) => {
  const [product, setProduct] = useState<QuizResurfaceProduct | null>(productDataProp ?? null);
  const [isCollapsed, setIsCollapsed] = useState(true);

  useEffect(() => {
    if (productDataProp !== undefined) {
      setProduct(productDataProp);
      return;
    }
    setProduct(readQuizResurfaceProduct());
  }, [productDataProp]);

  useEffect(() => {
    if (!product) return;
    document.body.classList.add("with-quiz-resurface");
    return () => document.body.classList.remove("with-quiz-resurface");
  }, [product]);

  if (!product) return null;

  const activePromo = product.promoName ? getPromoByName(product.promoName) : undefined;
  const promoLive = Boolean(product.promoName) && Boolean(activePromo);
  const displayPrice =
    promoLive && product.promoPrice != null ? product.promoPrice : product.bundledPrice;
  const savingsLabel = promoLive ? product.promoSavings : null;

  return (
    <div className={cn(styles.wrapper, OVERLAY_HERO ? styles.overlayHero : "")}>
      {isCollapsed ? (
        <div className="max-w-[1570px] mx-auto lg:px-[25px]">
          <div className={styles.banner}>
            <TextElementNova
              text="Your personalized routine <span class='whitespace-nowrap font-grilliBook'>is waiting.</span>"
              className="Title-10-Super-Normal text-center mb-1 text-nova-brand-primary-100"
            />
            <button
              type="button"
              aria-label="Check it out"
              className="block mx-auto text-nova-brand-primary-100"
              onClick={() => {
                segmentEvent("Banner Expanded", { name: "Quiz Product Recommendation" });
                setIsCollapsed(false);
              }}
            >
              <TextElementNova
                text="Check it out"
                className="Body-10-Dia-Strong text-nova-brand-primary-100 inline underline"
              />
              <span className="inline-block ml-1 relative top-[3px]">
                <ArrowIconTextlink />
              </span>
            </button>
          </div>
        </div>
      ) : (
        <QuizResurfaceBannerPanel
          product={product}
          displayPrice={displayPrice}
          savingsLabel={savingsLabel}
          promoLive={promoLive}
          onClose={() => {
            segmentEvent("Banner Closed", { name: "Quiz Product Recommendation" });
            setIsCollapsed(true);
          }}
        />
      )}
    </div>
  );
};

export default QuizResurfaceBanner;
